import React, { useEffect, useState } from "react";
import LoginForm from "./LoginForm";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, compose, Dispatch } from "redux";
import { useTheme } from "../../components/ThemeProvider";
import { SplitPageLayout } from "../../components/Layout";
import Page from "../../components/Page";
import {
  isAuthenticated,
  isLoginWithUnconfirmedUser,
  selectRequestData,
  selectRequestError,
  selectRequestState
} from "../../store/reducers";
import { useString as s } from "../../components/StringProvider";
import redirectToOAuthLogin from "../../utils/redirect-to-oauth-login";
import { selectAuthConfiguration } from "../../store/selectors/config";
import actionType from "../../store/actionTypes";
import Loader from "../../components/Loader";
import useLoadingState from "../../utils/use-loading-state";
import { login } from "../../store/actions/authentication";
import actionTypes from "../../store/actionTypes";

interface LocationState {
  from?: {
    pathname: string;
    search: string;
    hash: string;
  };
}

interface Invitation {
  discoveryId: string;
}

interface EmailStatus {
  email: string;
}

interface IdentityProviderRecord {
  identityProvider: string;
}

interface OwnProps {
  invitation?: Invitation;
}

type Props = OwnProps &
  ConnectedProps<typeof connector> &
  RouteComponentProps<{}, any, LocationState>;

const Login: React.FC<Props> = ({
  history,
  location,
  isAuthenticated,
  loadingState,
  loginError,
  isLoginWithUnconfirmedUser,
  authConfiguration,
  identityProviderRecord,
  login,
  ssoError,
  invitation,
  emailStatus
}) => {
  const [error, setError] = useState("");
  const loginErrorMessage = s(
    "login.page.messages.error",
    "Incorrect username or password"
  );

  const redirectTo =
    location && location.state && location.state.from
      ? location.state.from.pathname +
        location.state.from.search +
        location.state.from.hash
      : { pathname: "/MyDiscoveries" };

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath = invitation
        ? `/discoveries/${invitation.discoveryId}/select-challenges`
        : redirectTo;

      history.push(redirectPath as any);
    }
  }, [isAuthenticated, invitation]);

  useEffect(() => {
    if (identityProviderRecord) {
      redirectToOAuthLogin({
        stateData: location,
        identityProvider: identityProviderRecord.identityProvider
      });
    }
  }, [identityProviderRecord]);

  useLoadingState(
    loadingState,
    () => {},
    () => {
      console.log("loginError", loginError);

      if (isLoginWithUnconfirmedUser) {
        history.push("/verification-code");
      } else {
        setError(loginErrorMessage);
      }
    },
    () => {
      setError("");
    }
  );

  const onFinish = ({
    email,
    password
  }: {
    email: string;
    password: string;
  }) => {
    login({ email, password });
  };

  return !identityProviderRecord ? (
    <Page header={undefined}>
      <SplitPageLayout image={useTheme("assets.login_cover")}>
        <LoginForm
          error={error}
          ssoError={ssoError}
          onFinish={onFinish}
          invitation={invitation}
          email={emailStatus?.email}
        />
      </SplitPageLayout>
    </Page>
  ) : (
    <Loader />
  );
};

const mapStateToProps = (state: any) => {
  return {
    isAuthenticated: isAuthenticated(state),
    loadingState: selectRequestState(state, actionType.LOGIN_REQUEST),
    loginError: selectRequestError(state, actionType.LOGIN_REQUEST),
    isLoginWithUnconfirmedUser: isLoginWithUnconfirmedUser(state),
    authConfiguration: selectAuthConfiguration(state),
    identityProviderRecord: selectRequestData(
      state,
      actionType.IDENTITY_PROVIDER_SUCCESS
    ) as IdentityProviderRecord | undefined,
    ssoError: selectRequestError(state, actionType.SSO_LOGIN_FAILURE),
    emailStatus: selectRequestData(
      state,
      actionTypes.INVITATION_CHECK_EMAIL_STATUS_REQUEST
    ) as EmailStatus | undefined
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      login
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRouter, connector)(Login);
